/**
 * Favorites in the sidebar: the current user's pins (per user, per
 * workspace), through `usePins` / `usePinMutations`. Hidden entirely until
 * something is a favorite (mockup "what", point 2), and while the list is
 * still loading, so it never flashes. Reordered with the `reorder` mutation
 * and the order math in `pinOrder`: drag a row onto another (a line marks
 * where it lands), or its Move up / Move down controls from the keyboard. At either
 * end a control stays, marked disabled (`aria-disabled`, so it keeps focus),
 * rather than unmounting under the focus it just moved there.
 */

import { IconButton } from '@chakra-ui/react';
import type { MouseEventHandler, ReactElement } from 'react';
import { useNavigate } from 'react-router';
import type { PinDto } from '@bower/shared';

import { ROUTES } from '../../../constants/routes';
import { useAuth } from '../../../contexts/AuthContext';
import { usePinReorder } from '../../../hooks/data/pinOrder';
import { usePinMutations, usePins } from '../../../hooks/data/usePins';
import { useEntityNavigation } from '../../../hooks/entity/useEntityNavigation';
import { useCurrentUserQuery } from '../../../hooks/user/useUserQuery';
import { getHierarchyNodeGlyph } from '../../../theme/entityIcons';
import { MaterialIcon } from '../../ui/MaterialIcon';
import { viewTabStop } from '../../workspace/viewFocus';
import { SidebarRow, SidebarSection } from './SidebarSection';

function glyphFor(pin: PinDto) {
  return pin.entityType === 'project'
    ? getHierarchyNodeGlyph('project')
    : getHierarchyNodeGlyph('artifact', pin.artifactType ?? 'note');
}

/**
 * Removing a favorite unmounts its row, star (which had focus) and all. Hand
 * focus to the next favorite, else the previous one, else (the section goes
 * with its last row) the folder tree's tab stop, never to <body>. Run once
 * the removal has rendered.
 */
function focusAfterRemoving(star: HTMLElement) {
  const row = star.closest('li');
  const neighbor = (row?.nextElementSibling ?? row?.previousElementSibling)?.querySelector(
    'button',
  );
  const sidebar = star.closest('section')?.parentElement ?? null;
  requestAnimationFrame(() => {
    (neighbor?.isConnected ? neighbor : viewTabStop(sidebar))?.focus();
  });
}

interface RowControlProps {
  readonly label: string;
  readonly icon: ReactElement;
  readonly onClick: MouseEventHandler<HTMLButtonElement>;
  /** Move up / down at an end: marked disabled, focus kept, and it does nothing. */
  readonly atEnd?: boolean;
  readonly mr?: number;
}

/** A row's trailing control (Move up, Move down, Remove), shown on row hover or focus. */
function RowControl({ label, icon, onClick, atEnd = false, mr }: RowControlProps) {
  return (
    <IconButton
      aria-label={label}
      aria-disabled={atEnd || undefined}
      icon={icon}
      size="xs"
      variant="ghost"
      flexShrink={0}
      mr={mr}
      // Hidden until the row is hovered or the control focused. An end control
      // overrides the theme's disabled 0.4 at rest, and shows dimmed when revealed.
      opacity={0}
      _disabled={{ opacity: 0, cursor: 'default' }}
      _groupHover={{ opacity: atEnd ? 0.4 : 1 }}
      _focusVisible={{ opacity: atEnd ? 0.4 : 1 }}
      onClick={atEnd ? undefined : onClick}
    />
  );
}

export function FavoritesSection() {
  const navigate = useNavigate();
  const { openEntity } = useEntityNavigation({ fromWorkspace: true });
  const { user } = useAuth();
  const { data: currentUser } = useCurrentUserQuery(!!user);
  const userId = currentUser?.id ?? '';
  const { data: pins = [], isPending } = usePins(userId);
  const { unpin, reorder } = usePinMutations(userId);
  const { dragId, rowDrag, step } = usePinReorder(pins, reorder);

  if (isPending || pins.length === 0) return null;

  // A collection opens at its bare path; anything else through the shared
  // entity navigation, like every other surface.
  const open = (pin: PinDto) => {
    if (pin.entityType === 'project') navigate(ROUTES.collection(pin.entityId));
    else openEntity(pin.artifactType ?? 'note', pin.entityId, { surface: 'favorites' });
  };

  return (
    <SidebarSection label="Favorites" testId="sidebar-favorites">
      {pins.map((pin, index) => {
        const reorderRow = rowDrag(pin.id);
        return (
          <SidebarRow
            key={pin.id}
            label={pin.title}
            glyph={glyphFor(pin)}
            onOpen={() => open(pin)}
            drag={{
              onDragStart: (event) => {
                event.dataTransfer.effectAllowed = 'move';
                reorderRow.onDragStart();
              },
              onDragEnter: reorderRow.onDragEnter,
              onDragOver: (event) => {
                if (!dragId) return;
                event.preventDefault();
                event.dataTransfer.dropEffect = 'move';
              },
              onDrop: (event) => {
                if (!dragId) return;
                event.preventDefault();
                reorderRow.onDrop();
              },
              onDragEnd: reorderRow.onDragEnd,
              showDropLine: reorderRow.showDropLine,
            }}
            trailing={
              <>
                <RowControl
                  label={`Move ${pin.title} up`}
                  icon={<MaterialIcon name="arrow_upward" fontSize="14px" />}
                  atEnd={index === 0}
                  onClick={() => step(index, -1)}
                />
                <RowControl
                  label={`Move ${pin.title} down`}
                  icon={<MaterialIcon name="arrow_downward" fontSize="14px" />}
                  atEnd={index === pins.length - 1}
                  onClick={() => step(index, 1)}
                />
                <RowControl
                  label={`Remove ${pin.title} from favorites`}
                  icon={<MaterialIcon name="star" filled fontSize="14px" />}
                  mr={1}
                  onClick={(event) => {
                    focusAfterRemoving(event.currentTarget);
                    unpin.mutate(pin.id);
                  }}
                />
              </>
            }
          />
        );
      })}
    </SidebarSection>
  );
}
